import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { formatCurrency } from '../../utils/formatters';
import { Download, Eye, Send, Link, Unlink, Loader2, CheckCircle, AlertCircle, HelpCircle, Calendar, Layers, Search } from 'lucide-react';
import { format, subMonths } from 'date-fns';

// =============================================================================
// QBO STATUS (UNCHANGED)
// =============================================================================

function QboStatus({ onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    checkStatus();
    
    // Check for OAuth callback result
    const qboResult = searchParams.get('qbo');
    if (qboResult === 'success') {
      alert('QuickBooks connected successfully!');
      window.history.replaceState({}, '', '/admin/exports');
    } else if (qboResult === 'error') {
      const message = searchParams.get('message') || 'Connection failed';
      alert(`QuickBooks connection error: ${message}`);
      window.history.replaceState({}, '', '/admin/exports');
    }
  }, [searchParams]);

  const checkStatus = async () => {
    try {
      const data = await api.get('/qbo/status');
      setStatus(data);
      onStatusChange?.(data.connected);
    } catch (err) {
      console.error('Failed to check QBO status:', err);
      setStatus({ connected: false });
      onStatusChange?.(false);
    } finally {
      setLoading(false);
    }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const data = await api.get('/qbo/connect');
      // Redirect to Intuit OAuth
      window.location.href = data.authUrl;
    } catch (err) {
      alert('Failed to start QuickBooks connection: ' + err.message);
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!confirm('Disconnect from QuickBooks? You will need to reconnect to push bills.')) return;
    
    try {
      await api.post('/qbo/disconnect');
      setStatus({ connected: false });
      onStatusChange?.(false);
    } catch (err) {
      alert('Failed to disconnect: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Checking QuickBooks connection...
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {status?.connected ? (
        <>
          <span className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-4 h-4" />
            QuickBooks Connected
          </span>
          <button onClick={disconnect} className="text-sm text-gray-500 hover:text-red-600">
            <Unlink className="w-4 h-4" />
          </button>
        </>
      ) : (
        <button 
          onClick={connect} 
          disabled={connecting}
          className="btn-secondary text-sm"
        >
          {connecting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Link className="w-4 h-4" />
          )}
          Connect to QuickBooks
        </button>
      )}
    </div>
  );
}

// =============================================================================
// SOURCE SELECTOR
// =============================================================================

function SourceSelector({ value, onChange }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="text-sm text-gray-500">Source:</span>
      <div className="flex gap-1">
        <button
          onClick={() => onChange('schedule')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
            value === 'schedule' 
              ? 'bg-hub-600 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Calendar className="w-4 h-4" />
          Schedule
        </button>
        <button
          onClick={() => onChange('events')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
            value === 'events' 
              ? 'bg-hub-600 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Layers className="w-4 h-4" />
          Events
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// EVENT PICKER MODAL
// =============================================================================

function EventPickerModal({ isOpen, onClose, onSelect, selectedIds = [] }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(selectedIds);

  useEffect(() => {
    if (isOpen) {
      loadEvents();
      setSelected(selectedIds);
    }
  }, [isOpen, selectedIds]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      // Load events that have accepted quotes (ready for billing)
      const data = await api.get('/events?status=CONFIRMED,COMPLETED&hasAcceptedQuote=true');
      setEvents(data.events || data || []);
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(e => 
    e.name?.toLowerCase().includes(search.toLowerCase()) ||
    e.client?.name?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleEvent = (id) => {
    setSelected(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleConfirm = () => {
    onSelect(selected);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <h3 className="text-lg font-semibold mb-4">Select Events</h3>
        
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search events..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input pl-9"
          />
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 mb-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : filteredEvents.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              {events.length === 0 
                ? 'No confirmed events with accepted quotes found'
                : 'No events match your search'
              }
            </p>
          ) : (
            filteredEvents.map(event => (
              <label 
                key={event.id}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  selected.includes(event.id) 
                    ? 'bg-hub-50 border border-hub-200' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(event.id)}
                  onChange={() => toggleEvent(event.id)}
                  className="w-4 h-4 text-hub-600 rounded"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{event.name}</div>
                  <div className="text-sm text-gray-500">
                    {event.client?.name || 'No client'} 
                    {event.date && ` • ${format(new Date(event.date), 'MMM d, yyyy')}`}
                  </div>
                </div>
              </label>
            ))
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <span className="text-sm text-gray-500">
            {selected.length} event{selected.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button 
              onClick={handleConfirm} 
              className="btn-primary"
              disabled={selected.length === 0}
            >
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// EVENT BILLS EXPORT
// =============================================================================

function EventBillsExport() {
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qboConnected, setQboConnected] = useState(false);
  const [pushing, setPushing] = useState(false);

  useEffect(() => {
    api.get('/qbo/status')
      .then(data => setQboConnected(data.connected))
      .catch(() => setQboConnected(false));
  }, []);

  const loadPreview = async () => {
    if (selectedEventIds.length === 0) {
      alert('Please select at least one event');
      return;
    }
    
    setLoading(true);
    setPreview(null);
    try {
      const data = await api.post('/exports/events/bills/preview', { 
        eventIds: selectedEventIds 
      });
      setPreview(data);
    } catch (err) {
      alert(err.message || 'No vendor bills found for selected events');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/exports/events/bills?eventIds=${selectedEventIds.join(',')}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Download failed');
      }
      
      const blob = await response.blob();
      const filename = response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'event-bills.csv';
      
      const urlObj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(urlObj);
    } catch (err) {
      alert(err.message);
    }
  };

  const pushToQBO = async () => {
    if (!confirm(`Push ${preview.summary.vendorCount} vendor bills to QuickBooks?`)) return;
    
    setPushing(true);
    try {
      const result = await api.post('/qbo/push-event-bills', { 
        eventIds: selectedEventIds 
      });
      
      let message = result.message;
      if (result.results?.errors?.length > 0) {
        message += '\n\nErrors:\n' + result.results.errors.map(e => `${e.vendor}: ${e.error}`).join('\n');
      }
      
      alert(message);
      
      if (result.results?.success?.length > 0) {
        setPreview(null);
      }
    } catch (err) {
      alert('Failed to push to QuickBooks: ' + err.message);
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[250px]">
          <label className="label">Selected Events</label>
          <button
            onClick={() => setShowPicker(true)}
            className="input text-left w-full flex items-center justify-between"
          >
            <span className={selectedEventIds.length === 0 ? 'text-gray-400' : ''}>
              {selectedEventIds.length === 0 
                ? 'Click to select events...' 
                : `${selectedEventIds.length} event${selectedEventIds.length !== 1 ? 's' : ''} selected`
              }
            </span>
            <Layers className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <button 
          onClick={loadPreview} 
          className="btn-secondary" 
          disabled={loading || selectedEventIds.length === 0}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          Preview
        </button>
      </div>

      <EventPickerModal
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={setSelectedEventIds}
        selectedIds={selectedEventIds}
      />

      {preview && (
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <p className="text-sm text-gray-500">
                {preview.summary.vendorCount} vendors • {preview.summary.eventCount} events
              </p>
              <p className="text-xl font-bold">{formatCurrency(preview.summary.grandTotal)}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={downloadCSV} className="btn-secondary">
                <Download className="w-4 h-4" /> Download CSV
              </button>
              {qboConnected && (
                <button 
                  onClick={pushToQBO} 
                  className="btn-primary"
                  disabled={pushing}
                >
                  {pushing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send to QuickBooks
                </button>
              )}
            </div>
          </div>
          
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {preview.vendors?.map(v => (
              <div key={v.vendor.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between">
                  <span className="font-medium">{v.vendor.name}</span>
                  <span className="font-semibold">{formatCurrency(v.total)}</span>
                </div>
                <p className="text-sm text-gray-500 mb-2">
                  {v.vendor.category} • {v.lineItems.length} line items
                </p>
                <div className="space-y-1">
                  {v.lineItems.map((item, idx) => (
                    <div key={idx} className="text-xs text-gray-600 flex justify-between">
                      <span>{item.eventName} - {item.description}</span>
                      <span>{formatCurrency(item.cost)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// EVENT INVOICES EXPORT
// =============================================================================

function EventInvoicesExport() {
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qboConnected, setQboConnected] = useState(false);
  const [pushing, setPushing] = useState(false);

  useEffect(() => {
    api.get('/qbo/status')
      .then(data => setQboConnected(data.connected))
      .catch(() => setQboConnected(false));
  }, []);

  const loadPreview = async () => {
    if (selectedEventIds.length === 0) {
      alert('Please select at least one event');
      return;
    }
    
    setLoading(true);
    setPreview(null);
    try {
      const data = await api.post('/exports/events/invoices/preview', { 
        eventIds: selectedEventIds 
      });
      setPreview(data);
    } catch (err) {
      alert(err.message || 'No invoices found for selected events');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/exports/events/invoices?eventIds=${selectedEventIds.join(',')}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Download failed');
      }
      
      const blob = await response.blob();
      const filename = response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'event-invoices.csv';
      
      const urlObj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(urlObj);
    } catch (err) {
      alert(err.message);
    }
  };

  const pushToQBO = async () => {
    if (!confirm(`Push ${preview.summary.invoiceCount} invoices to QuickBooks?`)) return;
    
    setPushing(true);
    try {
      const result = await api.post('/qbo/push-event-invoices', { 
        eventIds: selectedEventIds 
      });
      
      let message = result.message;
      if (result.results?.errors?.length > 0) {
        message += '\n\nErrors:\n' + result.results.errors.map(e => `${e.event}: ${e.error}`).join('\n');
      }
      
      alert(message);
      
      if (result.results?.success?.length > 0) {
        setPreview(null);
      }
    } catch (err) {
      alert('Failed to push to QuickBooks: ' + err.message);
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[250px]">
          <label className="label">Selected Events</label>
          <button
            onClick={() => setShowPicker(true)}
            className="input text-left w-full flex items-center justify-between"
          >
            <span className={selectedEventIds.length === 0 ? 'text-gray-400' : ''}>
              {selectedEventIds.length === 0 
                ? 'Click to select events...' 
                : `${selectedEventIds.length} event${selectedEventIds.length !== 1 ? 's' : ''} selected`
              }
            </span>
            <Layers className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <button 
          onClick={loadPreview} 
          className="btn-secondary" 
          disabled={loading || selectedEventIds.length === 0}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          Preview
        </button>
      </div>

      <EventPickerModal
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={setSelectedEventIds}
        selectedIds={selectedEventIds}
      />

      {preview && (
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <p className="text-sm text-gray-500">
                {preview.summary.invoiceCount} invoices • {preview.summary.eventCount} events
              </p>
              <p className="text-xl font-bold">{formatCurrency(preview.summary.grandTotal)}</p>
              <p className="text-sm text-green-600">
                Margin: {formatCurrency(preview.summary.totalMargin)} ({preview.summary.marginPercent}%)
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={downloadCSV} className="btn-secondary">
                <Download className="w-4 h-4" /> Download CSV
              </button>
              {qboConnected && (
                <button 
                  onClick={pushToQBO} 
                  className="btn-primary"
                  disabled={pushing}
                >
                  {pushing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send to QuickBooks
                </button>
              )}
            </div>
          </div>
          
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {preview.invoices?.map(inv => (
              <div key={inv.event.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between">
                  <div>
                    <span className="font-medium">{inv.event.name}</span>
                    <span className="text-gray-500 text-sm ml-2">→ Bill to: {inv.client?.name || 'No client'}</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(inv.total)}</span>
                </div>
                <p className="text-sm text-gray-500 mb-2">
                  {inv.quote?.quoteNumber} • {inv.lineItems.length} line items • Margin: {formatCurrency(inv.margin)}
                </p>
                <div className="space-y-1">
                  {inv.lineItems.map((item, idx) => (
                    <div key={idx} className="text-xs text-gray-600 flex justify-between">
                      <span>
                        {item.description}
                        {item.quantity > 1 && ` (×${item.quantity})`}
                      </span>
                      <span>{formatCurrency(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// PAYROLL EXPORT
// =============================================================================

function PayrollExport() {
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [qboConnected, setQboConnected] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [markPaidDate, setMarkPaidDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [markingPaid, setMarkingPaid] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showUnmarkPaidModal, setShowUnmarkPaidModal] = useState(false);
  const [unmarkingPaid, setUnmarkingPaid] = useState(false);

  // Source toggle
  const [source, setSource] = useState('schedule');

  // Load available pay periods
  useEffect(() => {
    api.get('/exports/pay-periods')
      .then(data => {
        setPayPeriods(data);
        // Select current period by default (second item, first is future)
        if (data.length > 1) {
          setSelectedPeriod(data[1].index);
        } else if (data.length > 0) {
          setSelectedPeriod(data[0].index);
        }
      })
      .catch(err => console.error('Failed to load pay periods:', err))
      .finally(() => setLoadingPeriods(false));
  }, []);

  const loadPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      let url;
      if (useCustomRange) {
        url = `/exports/payroll/preview?startDate=${customStart}&endDate=${customEnd}`;
      } else {
        url = `/exports/payroll/preview?periodIndex=${selectedPeriod}`;
      }
      const data = await api.get(url);
      setPreview(data);
    } catch (err) {
      alert(err.message || 'No data found for this period');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    try {
      let url;
      if (useCustomRange) {
        url = `/api/exports/payroll?startDate=${customStart}&endDate=${customEnd}`;
      } else {
        url = `/api/exports/payroll?periodIndex=${selectedPeriod}`;
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}${url}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Download failed');
      }
      
      const blob = await response.blob();
      const filename = response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'payroll.csv';
      
      const urlObj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(urlObj);
    } catch (err) {
      alert(err.message);
    }
  };

  const pushToQBO = async () => {
    if (!confirm(`Push ${preview.summary.contractorCount} bills to QuickBooks?`)) return;
    
    setPushing(true);
    try {
      const body = useCustomRange 
        ? { startDate: customStart, endDate: customEnd }
        : { periodIndex: selectedPeriod };
      
      const result = await api.post('/qbo/push-bills', body);
      
      let message = result.message;
      if (result.results.errors.length > 0) {
        message += '\n\nErrors:\n' + result.results.errors.map(e => `${e.contractor}: ${e.error}`).join('\n');
      }
      
      alert(message);
      
      if (result.results.success.length > 0) {
        setPreview(null);
      }
    } catch (err) {
      alert('Failed to push to QuickBooks: ' + err.message);
    } finally {
      setPushing(false);
    }
  };

  const openMarkPaidModal = () => {
    // Check if this period was already marked as paid
    const selectedPeriodData = payPeriods.find(p => p.index === selectedPeriod);
    if (selectedPeriodData?.paidAt && !useCustomRange) {
      const paidDate = format(new Date(selectedPeriodData.paidAt), 'MMM d, yyyy');
      if (!confirm(`This period was already marked as paid on ${paidDate}.\n\nMark as paid again?`)) {
        return;
      }
    }
    setMarkPaidDate(format(new Date(), 'yyyy-MM-dd'));
    setShowMarkPaidModal(true);
  };

  const markAsPaid = async () => {
    if (!preview) return;
    setMarkingPaid(true);
    
    try {
      const params = {
        paidAt: new Date(markPaidDate + 'T12:00:00').toISOString()
      };
      
      if (useCustomRange) {
        params.startDate = customStart;
        params.endDate = customEnd;
      } else {
        params.periodIndex = selectedPeriod;
      }
      
      await api.post('/exports/payroll/mark-paid', params);
      alert(`Marked ${preview.summary.assignmentCount} assignments as paid on ${format(new Date(markPaidDate), 'MMM d, yyyy')}`);
      setPreview(null);
      setShowMarkPaidModal(false);
      // Reload pay periods to update paid status badges
      const updatedPeriods = await api.get('/exports/pay-periods');
      setPayPeriods(updatedPeriods);
    } catch (err) {
      alert(err.message);
    } finally {
      setMarkingPaid(false);
    }
  };

  const unmarkAsPaid = async () => {
    setUnmarkingPaid(true);
    try {
      const params = useCustomRange
        ? { startDate: customStart, endDate: customEnd }
        : { periodIndex: selectedPeriod };

      const result = await api.post('/exports/payroll/unmark-paid', params);
      alert(`Reset ${result.count} assignment(s) to unpaid. You can now push to QuickBooks again.`);
      setShowUnmarkPaidModal(false);
      setPreview(null);
      const updatedPeriods = await api.get('/exports/pay-periods');
      setPayPeriods(updatedPeriods);
    } catch (err) {
      alert(err.message);
    } finally {
      setUnmarkingPaid(false);
    }
  };

  const selectedPeriodLabel = payPeriods.find(p => p.index === selectedPeriod)?.label || '';

  return (
    <div className="card">
      <div className="card-header flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold">Contractor Payroll (Bills)</h2>
          <div className="relative">
            <button 
              onClick={() => setShowHelp(!showHelp)}
              className="text-gray-400 hover:text-gray-600"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
            {showHelp && (
              <div className="absolute left-0 top-8 z-50 w-80 p-4 bg-white border border-gray-200 rounded-lg shadow-lg text-sm text-gray-600">
                <button 
                  onClick={() => setShowHelp(false)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
                <p className="mb-2">
                  <strong>Mark as Paid</strong> places a message in the contractor's Pay page letting them know that payroll from the selected pay period was processed.
                </p>
                <p className="mb-2">
                  It also places a <span className="text-green-600">✓ Paid</span> indicator next to the pay period to remind you that the bills have been sent to QuickBooks.
                </p>
                <p>
                  You can still access the data, export the CSV, and even Send to QuickBooks again. <strong>Doing so will create duplicate entries</strong>, so make sure that's your intention.
                </p>
              </div>
            )}
          </div>
        </div>
        <QboStatus onStatusChange={setQboConnected} />
      </div>
      <div className="card-body space-y-4">
        {/* Source Selector */}
        <SourceSelector value={source} onChange={setSource} />
        
        {/* Conditional rendering based on source */}
        {source === 'events' ? (
          <EventBillsExport />
        ) : (
          <>
            {/* Period Type Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setUseCustomRange(false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !useCustomRange 
                    ? 'bg-hub-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Pay Period
              </button>
              <button
                onClick={() => setUseCustomRange(true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  useCustomRange 
                    ? 'bg-hub-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Custom Date Range
              </button>
            </div>

            {/* Period Selection */}
            {!useCustomRange ? (
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[250px]">
                  <label className="label">Pay Period (bi-weekly from Dec 15)</label>
                  {loadingPeriods ? (
                    <div className="input bg-gray-50">Loading...</div>
                  ) : (
                    <select 
                      value={selectedPeriod ?? ''} 
                      onChange={e => setSelectedPeriod(parseInt(e.target.value))}
                      className="input"
                    >
                      {payPeriods.map(p => (
                        <option key={p.index} value={p.index}>
                          {p.label}{p.paidAt ? ' ✓ Paid' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <button 
                  onClick={loadPreview} 
                  className="btn-secondary" 
                  disabled={loading || selectedPeriod === null}
                >
                  <Eye className="w-4 h-4" /> Preview
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="label">Start Date</label>
                  <input 
                    type="date" 
                    value={customStart} 
                    onChange={e => setCustomStart(e.target.value)} 
                    className="input" 
                  />
                </div>
                <div>
                  <label className="label">End Date</label>
                  <input 
                    type="date" 
                    value={customEnd} 
                    onChange={e => setCustomEnd(e.target.value)} 
                    className="input" 
                  />
                </div>
                <button 
                  onClick={loadPreview} 
                  className="btn-secondary" 
                  disabled={loading || !customStart || !customEnd}
                >
                  <Eye className="w-4 h-4" /> Preview
                </button>
              </div>
            )}

            {/* Preview Results */}
            {preview && (
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      {preview.summary.contractorCount} contractors • {preview.summary.assignmentCount} assignments
                    </p>
                    <p className="text-xl font-bold">{formatCurrency(preview.summary.grandTotal)}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={downloadCSV} className="btn-secondary">
                      <Download className="w-4 h-4" /> Download CSV
                    </button>
                    {qboConnected && (
                      <button 
                        onClick={pushToQBO} 
                        className="btn-primary"
                        disabled={pushing}
                      >
                        {pushing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Send to QuickBooks
                      </button>
                    )}
                    <button onClick={openMarkPaidModal} className="btn-secondary">
                      Mark All Paid
                    </button>
                    <button
                      onClick={() => setShowUnmarkPaidModal(true)}
                      className="btn-secondary text-amber-600 border-amber-300 hover:bg-amber-50"
                    >
                      Unmark as Paid
                    </button>
                  </div>
                </div>
                
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {preview.contractors.map(c => (
                    <div key={c.contractor.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between">
                        <span className="font-medium">{c.contractor.name}</span>
                        <span className="font-semibold">{formatCurrency(c.total)}</span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">{c.assignments.length} gigs</p>
                      <div className="space-y-1">
                        {c.assignments.map(a => {
                          const dateStr = typeof a.date === 'string' ? a.date.substring(0, 10) : a.date.toISOString().substring(0, 10);
                          const [year, month, day] = dateStr.split('-');
                          const dateDisplay = `${parseInt(month)}/${parseInt(day)}`;
                          const formatTime12 = (time24) => {
                            if (!time24 || typeof time24 !== 'string' || !time24.includes(':')) return '';
                            const [hours, minutes] = time24.split(':').map(Number);
                            if (isNaN(hours) || isNaN(minutes)) return '';
                            const period = hours >= 12 ? 'PM' : 'AM';
                            const hours12 = hours % 12 || 12;
                            return `${hours12}:${String(minutes).padStart(2, '0')}${period}`;
                          };
                          const timeDisplay = a.startTime && a.endTime 
                            ? ` ${formatTime12(a.startTime)}-${formatTime12(a.endTime)}`.replace(/^ -$/, '')
                            : '';
                          return (
                            <div key={a.id} className="text-xs text-gray-600 flex justify-between">
                              <span>{dateDisplay}{timeDisplay} - {a.location}</span>
                              <span>{formatCurrency(a.payout)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mark as Paid Modal */}
            {showMarkPaidModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                  <h3 className="text-lg font-semibold mb-4">Mark Payroll as Paid</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This will mark {preview?.summary?.assignmentCount || 0} assignments as paid.
                  </p>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Date
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="paidDateOption"
                          checked={markPaidDate === format(new Date(), 'yyyy-MM-dd')}
                          onChange={() => setMarkPaidDate(format(new Date(), 'yyyy-MM-dd'))}
                          className="text-hub-600"
                        />
                        <span className="text-sm">Today ({format(new Date(), 'MMM d, yyyy')})</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="paidDateOption"
                          checked={markPaidDate !== format(new Date(), 'yyyy-MM-dd')}
                          onChange={() => {}}
                          className="text-hub-600"
                        />
                        <span className="text-sm">Select date:</span>
                        <input
                          type="date"
                          value={markPaidDate}
                          onChange={(e) => setMarkPaidDate(e.target.value)}
                          className="input py-1 px-2 text-sm"
                        />
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowMarkPaidModal(false)}
                      className="btn-secondary"
                      disabled={markingPaid}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={markAsPaid}
                      className="btn-primary"
                      disabled={markingPaid}
                    >
                      {markingPaid ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Marking...
                        </>
                      ) : (
                        'Mark as Paid'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Unmark as Paid Modal */}
            {showUnmarkPaidModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4 shadow-xl">
                  <h3 className="text-lg font-semibold mb-2">Unmark as Paid?</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    This will reset all assignments in this pay period back to unpaid,
                    allowing them to be pushed to QuickBooks again.
                  </p>
                  <div className="text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-4">
                    ⚠️ If you already pushed these to QuickBooks, pushing again will create duplicate bills.
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Period: <strong>{useCustomRange ? `${customStart} – ${customEnd}` : selectedPeriodLabel}</strong>
                  </p>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setShowUnmarkPaidModal(false)}
                      className="btn-secondary"
                      disabled={unmarkingPaid}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={unmarkAsPaid}
                      disabled={unmarkingPaid}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2"
                    >
                      {unmarkingPaid ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Yes, Unmark as Paid
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// INVOICE EXPORT
// =============================================================================

function InvoiceExport() {
  const now = new Date();
  const [startDate, setStartDate] = useState(format(subMonths(now, 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(now, 'yyyy-MM-dd'));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qboConnected, setQboConnected] = useState(false);
  const [pushing, setPushing] = useState(false);
  
  // Source toggle
  const [source, setSource] = useState('schedule');

  // Check QBO status
  useEffect(() => {
    api.get('/qbo/status')
      .then(data => setQboConnected(data.connected))
      .catch(() => setQboConnected(false));
  }, []);

  const loadPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const data = await api.get(`/exports/invoices/preview?startDate=${startDate}&endDate=${endDate}`);
      setPreview(data);
    } catch (err) {
      alert(err.message || 'No data found for this period');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/exports/invoices?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Download failed');
      }
      
      const blob = await response.blob();
      const filename = response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'invoices.csv';
      
      const urlObj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(urlObj);
    } catch (err) {
      alert(err.message);
    }
  };

  const pushToQBO = async () => {
    if (!confirm(`Push ${preview.summary.locationCount} invoices to QuickBooks?`)) return;
    
    setPushing(true);
    try {
      const result = await api.post('/qbo/push-invoices', { startDate, endDate });
      
      let message = result.message;
      if (result.results.errors.length > 0) {
        message += '\n\nErrors:\n' + result.results.errors.map(e => `${e.client}: ${e.error}`).join('\n');
      }
      
      alert(message);
      
      if (result.results.success.length > 0) {
        setPreview(null);
      }
    } catch (err) {
      alert('Failed to push to QuickBooks: ' + err.message);
    } finally {
      setPushing(false);
    }
  };

  const markExported = async () => {
    if (!preview) return;
    if (!confirm(`Mark ${preview.summary.gigCount} gigs as exported?`)) return;
    
    const ids = preview.locations.flatMap(loc => loc.gigs.map(g => g.id));
    try {
      await api.post('/exports/invoices/mark-exported', { gigIds: ids });
      alert('Marked as exported!');
      setPreview(null);
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="card">
      <div className="card-header flex justify-between items-center">
        <h2 className="font-semibold">Client Invoices</h2>
        {qboConnected && (
          <span className="flex items-center gap-2 text-green-600 text-sm">
            <CheckCircle className="w-4 h-4" />
            QuickBooks Connected
          </span>
        )}
      </div>
      <div className="card-body space-y-4">
        {/* Source Selector */}
        <SourceSelector value={source} onChange={setSource} />
        
        {/* Conditional rendering based on source */}
        {source === 'events' ? (
          <EventInvoicesExport />
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="label">Start Date</label>
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={e => setStartDate(e.target.value)} 
                  className="input" 
                />
              </div>
              <div>
                <label className="label">End Date</label>
                <input 
                  type="date" 
                  value={endDate} 
                  onChange={e => setEndDate(e.target.value)} 
                  className="input" 
                />
              </div>
              <button onClick={loadPreview} className="btn-secondary" disabled={loading}>
                <Eye className="w-4 h-4" /> Preview
              </button>
            </div>

            {preview && (
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      {preview.summary.locationCount} invoices • {preview.summary.gigCount} gigs
                    </p>
                    <p className="text-xl font-bold">{formatCurrency(preview.summary.grandTotal)}</p>
                    <p className="text-sm text-green-600">
                      Production Fee: {formatCurrency(preview.summary.totalProductionFee)} ({preview.summary.marginPercent}%)
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={downloadCSV} className="btn-secondary">
                      <Download className="w-4 h-4" /> Download CSV
                    </button>
                    {qboConnected && (
                      <button 
                        onClick={pushToQBO} 
                        className="btn-primary"
                        disabled={pushing}
                      >
                        {pushing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Send to QuickBooks
                      </button>
                    )}
                    <button onClick={markExported} className="btn-secondary">
                      Mark Exported
                    </button>
                  </div>
                </div>
                
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {preview.locations.map(loc => (
                    <div key={loc.location.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between">
                        <div>
                          <span className="font-medium">{loc.location.name}</span>
                          <span className="text-gray-500 text-sm ml-2">→ Bill to: {loc.venue.name}</span>
                        </div>
                        <span className="font-semibold">{formatCurrency(loc.total)}</span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        {loc.gigs.length} gigs • Fee: {formatCurrency(loc.productionFee)}
                      </p>
                      <div className="space-y-1">
                        {loc.gigs.map(g => {
                          const dateDisplay = g.dateFormatted || (() => {
                            const d = g.date.substring(0, 10).split('-');
                            return `${parseInt(d[1])}/${parseInt(d[2])}`;
                          })();
                          const formatTime12 = (time24) => {
                            if (!time24 || typeof time24 !== 'string' || !time24.includes(':')) return '';
                            const [hours, minutes] = time24.split(':').map(Number);
                            if (isNaN(hours) || isNaN(minutes)) return '';
                            const period = hours >= 12 ? 'PM' : 'AM';
                            const hours12 = hours % 12 || 12;
                            return `${hours12}:${String(minutes).padStart(2, '0')}${period}`;
                          };
                          const timeDisplay = g.timeSlot ? ` ${g.timeSlot}` : '';
                          return (
                            <div key={g.id} className="text-xs text-gray-600 flex justify-between">
                              <span>
                                {dateDisplay}{timeDisplay}
                                {g.performers?.length > 0 && ` - ${g.performers.join(', ')}`}
                              </span>
                              <span>{formatCurrency(g.billed)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN EXPORT
// =============================================================================

export default function AccountantExports() {
  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="text-2xl font-bold">Exports</h1>
      <PayrollExport />
      <InvoiceExport />
    </div>
  );
}
